/**
 * P17-T11 — `CodCollectedModal`. Confirms `out_for_delivery → paid` for
 * a COD order and POSTs to `/v1/admin/orders/:id/cod-collected` (empty
 * body). The 200 response (`CodCollectedResponse`) carries the new
 * invoice id + number + payment id + collected_at timestamp.
 *
 * UX intent (spec §8.2): operator confirms physical receipt of the
 * total amount in cash. The modal surfaces the order's `total_minor`
 * formatted as GBP so the operator can sanity-check before clicking
 * through. No editable fields — collection is all-or-nothing (Q3-C).
 *
 * Idempotency: api-client auto-generates a fresh `idempotency-key` per
 * POST. The COD endpoint replays the 200 response on a duplicate call
 * within the IdempotencyGuard window (P15 convention).
 */
import { useState } from "react";
import { Button, Modal, Numeric } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminOrderDetailDTO,
  CodCollectedResponse,
} from "@wmart/shared-types";

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

export function CodCollectedModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const [submitting, setSubmitting] = useState(false);

  async function onConfirm() {
    setSubmitting(true);
    try {
      const res = await api.post<CodCollectedResponse>(
        `/v1/admin/orders/${order.id}/cod-collected`,
        {},
      );
      notify(
        "success",
        `Cash collected — invoice ${res.invoice_number} issued`,
      );
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Mark collected failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Confirm cash collected"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Back
          </Button>
          <Button
            onClick={() => void onConfirm()}
            disabled={submitting}
            loading={submitting}
          >
            Confirm collected
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-body-sm text-ink-700">
          Confirm receipt of the full cash amount for order{" "}
          <span className="font-mono">{order.id.slice(0, 8)}…</span>. This
          marks the order as <strong>paid</strong>, issues a VAT invoice
          (as receipt), and records a payment row.
        </p>

        <div className="rounded-sm bg-paper-warm p-4">
          <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
            Amount to collect
          </p>
          <p className="mt-1 font-serif text-display-sm text-ink-900">
            <Numeric serif>{formatGBP(order.total_minor)}</Numeric>
          </p>
        </div>

        <p className="text-caption text-ink-500">
          This action cannot be undone. Use{" "}
          <strong>Mark COD refused</strong> instead if the buyer declined
          the goods and the cash was not handed over.
        </p>
      </div>
    </Modal>
  );
}
